import { useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Typography,
  Space,
  Breadcrumb,
  Card,
  Descriptions,
  Tag,
  Spin,
  Alert,
  Tabs,
  Button,
  Statistic,
  Row,
  Col,
  Tooltip,
} from 'antd';
import {
  HomeOutlined,
  NodeIndexOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { useJobDetail, useJobStats } from '../api/queries/useJobs';
import { useTenant } from '../hooks/useTenant';
import { JobGraph } from '../components/jobs/JobGraph';
import { AwsResourceLinks } from '../components/aws/AwsResourceLinks';
import { getErrorMessage } from '../utils/error';
import { getJobStatusLabel, getJobStatusTagColor } from '../utils/jobStatus';

dayjs.extend(duration);

const { Title, Text, Paragraph } = Typography;

const STATUS_ICONS: Record<string, React.ReactNode> = {
  JOB_END: <CheckCircleOutlined style={{ color: '#52c41a' }} />,
  JOB_END_EXCEPTION: <CloseCircleOutlined style={{ color: '#ff4d4f' }} />,
};

export function JobDetailPage() {
  const navigate = useNavigate();
  const { jobId } = useParams<{ jobId: string }>();
  const { tenantId, isLoading: isTenantLoading } = useTenant();
  const [activeTab, setActiveTab] = useState('graph');

  const { data: jobData, isLoading: isJobLoading, error: jobError } = useJobDetail(jobId, true);
  const { data: statsData, isLoading: isStatsLoading } = useJobStats(jobId);
  const [highlightedEventId, setHighlightedEventId] = useState<string | null>(null);

  // Handle graph node click - navigate to event timeline and scroll to event
  const handleGraphNodeClick = useCallback((_nodeId: string, data: unknown) => {
    const nodeData = data as { spanId?: string; status?: string };
    const spanId = nodeData?.spanId;

    if (spanId) {
      // Switch to events tab
      setActiveTab('events');
      setHighlightedEventId(spanId);

      // Scroll to the event after a brief delay to allow tab switch
      setTimeout(() => {
        const element = document.getElementById(`event-${spanId}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);

      // Clear highlight after a few seconds
      setTimeout(() => {
        setHighlightedEventId(null);
      }, 3000);
    }
  }, []);

  if (isTenantLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!tenantId) {
    return (
      <Alert
        type="warning"
        message="No Tenant Selected"
        description="Please select a tenant from the header."
        showIcon
      />
    );
  }

  if (isJobLoading) {
    return (
      <div style={{ textAlign: 'center', padding: 48 }}>
        <Spin size="large" />
        <Paragraph style={{ marginTop: 16 }}>Loading job details...</Paragraph>
      </div>
    );
  }

  if (jobError) {
    return (
      <Alert
        type="error"
        message="Error loading job"
        description={`Error: ${getErrorMessage(jobError)}`}
        showIcon
      />
    );
  }

  if (!jobData) {
    return (
      <Alert
        type="warning"
        message="Job not found"
        description={`No job found with ID: ${jobId}`}
        showIcon
      />
    );
  }

  const { job, events } = jobData;
  const finalStatus = job.status;
  const duration_ms = job.duration_ms || 0;
  const formattedDuration = duration_ms > 1000
    ? `${(duration_ms / 1000).toFixed(2)}s`
    : `${duration_ms}ms`;

  const tabItems = [
    {
      key: 'graph',
      label: (
        <span>
          <NodeIndexOutlined />
          Execution Graph
        </span>
      ),
      children: (
        <JobGraph
          events={events}
          height={500}
          onNodeClick={handleGraphNodeClick}
        />
      ),
    },
    {
      key: 'events',
      label: (
        <span>
          <ClockCircleOutlined />
          Event Timeline ({events.length})
        </span>
      ),
      children: (
        <Card>
          {events.map((event, index) => {
            const eventId = event.span_id || `${event.job_id}-${index}`;
            const isHighlighted = highlightedEventId === event.span_id;

            return (
              <Card.Grid
                id={`event-${eventId}`}
                key={`${event.span_id || event.job_id}-${event.time}-${index}`}
                style={{
                  width: '100%',
                  padding: 12,
                  backgroundColor: isHighlighted ? '#e6f7ff' : undefined,
                  transition: 'background-color 0.3s ease',
                  borderLeft: isHighlighted ? '3px solid #1890ff' : undefined,
                }}
              >
                <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                  <Space>
                    <Tag color={getJobStatusTagColor(event.status)}>
                      {event.status}
                    </Tag>
                    <Text>{event.details || '-'}</Text>
                  </Space>
                  <Space>
                    <Text type="secondary">
                      {dayjs(event.time).format('HH:mm:ss.SSS')}
                    </Text>
                    {event.span_id && (
                      <Tooltip title={event.span_id}>
                        <Tag>{event.span_id.substring(0, 8)}...</Tag>
                      </Tooltip>
                    )}
                  </Space>
                </Space>
              </Card.Grid>
            );
          })}
        </Card>
      ),
    },
  ];

  return (
    <div>
      <Breadcrumb
        items={[
          {
            href: '/',
            title: (
              <>
                <HomeOutlined />
                <span>Dashboard</span>
              </>
            ),
          },
          {
            title: (
              <>
                <NodeIndexOutlined />
                <span>Job Details</span>
              </>
            ),
          },
        ]}
        style={{ marginBottom: 16 }}
      />

      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Space style={{ justifyContent: 'space-between', width: '100%' }} wrap>
          <Space>
            {STATUS_ICONS[finalStatus] || <ClockCircleOutlined />}
            <Title level={3} style={{ margin: 0 }}>
              Job Details
            </Title>
          </Space>
          <AwsResourceLinks
            handlerArn={job.handler_arn}
            traceId={job.trace_id}
            jobId={job.job_id}
            startTime={job.start_time}
            endTime={job.end_time}
          />
        </Space>

        <Card title="Job Information">
          <Descriptions column={{ xs: 1, sm: 2, md: 3 }} bordered size="small">
            <Descriptions.Item label="Job ID">
              <Space>
                <Text copyable={{ text: job.job_id }}>{job.job_id}</Text>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              <Tag color={getJobStatusTagColor(finalStatus)}>
                {getJobStatusLabel(finalStatus)}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Namespace">
              <Tag>{job.namespace}</Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Trace ID">
              <Space>
                <Text copyable={{ text: job.trace_id }}>{job.trace_id}</Text>
                <Button
                  type="link"
                  size="small"
                  icon={<LinkOutlined />}
                  onClick={() => navigate(`/search?q=${job.trace_id}`)}
                >
                  Search
                </Button>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Start Time">
              {job.start_time
                ? dayjs(job.start_time).format('YYYY-MM-DD HH:mm:ss.SSS')
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="End Time">
              {job.end_time
                ? dayjs(job.end_time).format('YYYY-MM-DD HH:mm:ss.SSS')
                : '-'}
            </Descriptions.Item>
            <Descriptions.Item label="Duration">
              <Text strong>{formattedDuration}</Text>
            </Descriptions.Item>
          </Descriptions>
        </Card>

        {/* Stats Card */}
        {isStatsLoading ? (
          <Card title="Job Statistics" loading />
        ) : statsData && statsData.stats.length > 0 ? (
          <Card title="Job Statistics">
            <Row gutter={[16, 16]}>
              {statsData.stats.map((stat, index) => (
                <Col xs={12} sm={8} md={6} key={`${stat.metric_name}-${index}`}>
                  <Statistic
                    title={stat.metric_name}
                    value={stat.total}
                    suffix={stat.unit}
                    precision={stat.unit === 'ms' ? 0 : 2}
                  />
                  {stat.count > 1 && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Avg: {stat.avg.toFixed(2)} | Min: {stat.min} | Max: {stat.max}
                    </Text>
                  )}
                </Col>
              ))}
            </Row>
          </Card>
        ) : null}

        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={tabItems}
          size="large"
        />
      </Space>
    </div>
  );
}
